import dialog from 'devextreme/ui/dialog';
import Data from 'app-core/Data';
import Auth from 'src/auth.js';
import moment from 'moment';
import azure from './azure.js';
import Localization from 'devextreme/localization.js';
import Vue from 'vue';
import storage from 'storage';
import signalR from './signalr';
import tabsManager from './tabs-manager';
import localDb from 'app-core/local-db';
import audioHelper from 'view/audioTracker/audio-helper';

let config = {};
let appVersion = '1.0.0.23';

let vueApp = null;

let notifier = null;

let preventNotifications = false;

const eventsBus = new Vue();
const creativeBlobsDb = localDb.initStore('creative-blobs-local-db');

const globals = {
	recorderJs: {
		bufferLen: 16384
	}
}

function getSetting(dotSplit) {
	let split = dotSplit && dotSplit.split('.') || [];
	let currentObject = config.settings;
	while (split.length > 0 && currentObject) {
		currentObject = currentObject[split[0]];
		split.splice(0, 1);
	}
	return currentObject;
}

function notifySuccess(text, time) {
	if (!notificationsVisible()) return;
	notifier?.notify({
		message: text,
		type: 'success',
		displayTime: time || 5000
	});
}

function notifyError(text, time) {
	if (!notificationsVisible()) return;
	notifier?.notify({
		message: text,
		type: 'error',
		displayTime: time || 7000
	});
}

function notifyInfo(text, time) {
	if (!notificationsVisible()) return;
	notifier?.notify({
		message: text,
		type: 'info',
		displayTime: time || 5000
	});
}

function notifyWarning(text, time) {
	if (!notificationsVisible()) return;
	notifier?.notify({
		message: text,
		type: 'warning',
		displayTime: time || 5000
	});
}

function notifyMessage(text, opts) {
	if (!notificationsVisible()) return;
	notifier?.notify({
		...opts,
		message: text,
		type: opts?.type || 'success',
		displayTime: opts?.time || 5000
	});
	if (opts?.busy !== null)
		vueApp?.$eventsBus?.$emit('notification-busy', opts?.busy);
	vueApp?.$eventsBus?.$emit('notification-message', { ...opts, message: text });
}

function notify(message, type, displayTime) {
	if (!notificationsVisible()) return;
	notifier?.notify({
		message,
		type: type || 'success',
		displayTime: displayTime || 5000
	});
}

function confirmMessage(opts) {
	if (!notificationsVisible()) return;
	let dlg = dialog.custom({
		...opts,
		messageHtml: opts.text || opts.message,
		title: opts.title,
		buttons: opts.buttons || [{
			text: 'Yes',
			onClick: (e) => {
				return true;
			}
		}, {
			text: 'No',
			onClick: (e) => {
				return false;
			}
		}]
	});

	let result = dlg.show();
	result.then((dialogResult) => {
		if (opts.onExecute)
			opts.onExecute(dialogResult);
		if (dialogResult && opts.handler)
			opts.handler(dialogResult);
		else if (!dialogResult && opts.cancel)
			opts.cancel(dialogResult);
	});

	return dlg;
}

function confirmAction(title, msg, opts) {
	if (!notificationsVisible()) return;
	let autoCloseSeconds = opts?.timeout;
	return new Promise((res, rej) => {
		let messageId = Data.uuid();
		let dlg = confirmMessage({
			title: title || getVueApp().$tCaption('Confirm action', '@actions'),
			text: `<div id="messageBody_${messageId}">${msg || getVueApp().$tCaption('Do you want to remove item?', '@actions')}</div><div class="adthos-dialog-timer-message" id="messageTimer_${messageId}"></div>`,
			handler: () => {
				res(true);
			},
			cancel: () => rej(false),
			...(opts || {})
		});
		let updateMessage = () => document.querySelector(`#messageTimer_${messageId}`).innerHTML = getVueApp().$tCaption(`This message will be closed in ${autoCloseSeconds} seconds.`);
		let checkTimer = () => {
			if (autoCloseSeconds > 0) {
				updateMessage();
				setTimeout(() => {
					if (!autoCloseSeconds) return;

					autoCloseSeconds--;
					if (autoCloseSeconds <= 0) {
						updateMessage();
						if (opts?.onTimeout) {
							opts.onTimeout()
						}
						res(true);
						dlg.hide();
					} else {
						checkTimer();
					}
				}, 1000);
			}
		}
		if (autoCloseSeconds) {
			checkTimer();
		}
	}).finally(() => autoCloseSeconds = null);
}

function getData() {
	return Data;
}

function getAuth() {
	return Auth;
}

function momentFormat(format, convertToDxFormat) {
	let momentFormat = `${moment.localeData().longDateFormat(format)}`;
	if (convertToDxFormat) {
		momentFormat = momentFormat
			.replace(new RegExp(' D', 'g'), '')
			.replace(new RegExp('Y', 'g'), 'y');
	}
	return momentFormat;
}

function formatNumber(num, precision, type) {
	if (!type && (num || num === 0) && num.toLocaleString) {
		let localeDecimalSeparator = 0.12 .toLocaleString().charAt(1),
		localeThousandSeparator = 1000 .toLocaleString().charAt(1);
		if (localeThousandSeparator === '0')
			localeThousandSeparator = localeDecimalSeparator === "." ? "," : ".";

		if (precision === -1)
			precision = `${num.toString().split('.')[1]}`.length;
		if (precision === void 0)
			precision = 2;

		let isNegative = num < 0,
			f = num.toFixed(precision).slice(+isNegative);

		return precision <= 0 ? 
			(isNegative ? '-' : '') + f.replace(/(?=(?!^)(?:\d{3})+(?!\d))/g, localeThousandSeparator) 
			: (isNegative ? '-' : '') + f.slice(0,-(precision + 1)).replace(/(?=(?!^)(?:\d{3})+(?!\d))/g, localeThousandSeparator) + localeDecimalSeparator + f.slice(-precision);
	}
	return Localization.formatNumber(num, {
		type: type || 'fixedPoint',
		precision: precision || 2
	});
}

function currencySign(currency) {
	let signs = {
		'USD': '$',
		'EUR': '€'
	};
	return signs[`${currency}`.toUpperCase()] || currency;
}

function formatCell(row, field, style) {
	const formatter = new Intl.NumberFormat('en', {
		style,
		currency: row.CurrencyCode,
		minimumFractionDigits: 2
	});

	return style === 'percent' ? `${row[field]}%` : formatter.format(row[field]);
}

function setNotifier(notifierInstance) {
	notifier = notifierInstance;
}

function setTitle(title) {
	document.title = `${vueApp.$tCaption('Adthos Platform')}${(title && ` - ${title}`) || ''}`;
}

function setPreventNotifications(v) {
	preventNotifications = v;
}

function notificationsVisible() {
	return !preventNotifications && (tabsManager.instance.isInitiatedTab || tabsManager.instance.isFocusedTab);
}

function setConfig(c) {
	config = c;
	if (__DEBUG__)
		console.log('Config loaded', config);
}

function checkVersion(clear) {
	let result = window.localStorage['app-version'] === appVersion;
	if (!result) {
		if (__DEBUG__)
			console.warn('New app version detected');
		if (clear) {
			window.localStorage.clear();
			window.localStorage['app-version'] = appVersion;
		}
	}
	return result;
}

function setVueApp(v) {
	vueApp = v;
}

function getVueApp() {
	return vueApp;
}

function isAICreative() {
	return vueApp.$route.path.indexOf('easy-creative') > -1;
}

async function releaseUI() {
	await new Promise((resolve) => setTimeout(resolve, 0));
}

let lastLog = null;
function logOnce(params, period = 100) {
	if (!lastLog || (moment().diff(lastLog, 'miliseconds') >= period)) {
		console.log(...params);
		lastLog = new Date();
	}
}

tabsManager.initTab();

export {
	setConfig,
	checkVersion,
	setVueApp,
	getVueApp,
	eventsBus,
	setNotifier,
	notify,
	notifyError,
	notifyInfo,
	notifyMessage,
	notifySuccess,
	notifyWarning,
	setPreventNotifications,
	confirmAction,
	isAICreative
}

export default {
	install(Vue) {
		Object.defineProperties(Vue.prototype, {
			$settings: {
				get: function () {
					return config.settings;
				}
			},
			$getSetting: {
				get: function () {
					return getSetting;
				}
			},
			$notify: {
				get: function () {
					return notify;
				}
			},
			$notifySuccess: {
				get: function () {
					return notifySuccess;
				}
			},
			$notifyMessage: {
				get: function () {
					return notifyMessage;
				}
			},
			$notifyInfo: {
				get: function () {
					return notifyInfo;
				}
			},
			$notifyError: {
				get: function () {
					return notifyError;
				}
			},
			$notifyWarning: {
				get: function () {
					return notifyWarning;
				}
			},
			$confirmMessage: {
				get: function () {
					return confirmMessage;
				}
			},
			$confirm: {
				get: function() {
					return confirmAction;
				}
			},
			$Data: {
				get: function () {
					return getData();
				}
			},
			$auth: {
				get: function () {
					return getAuth();
				}
			},
			$moment: {
				get: function () {
					return moment;
				}
			},
			$azure: {
				get: function () {
					return azure;
				}
			},
			$momentFormat: {
				get: function () {
					return momentFormat;
				}
			},
			$formatNumber: {
				get: function () {
					return formatNumber;
				}
			},
			$currencySign: {
				get: function () {
					return currencySign;
				}
			},
			$formatCell: {
				get: function () {
					return formatCell;
				}
			},
			$eventsBus: {
				get: function () {
					return eventsBus;
				}
			},
			$storage: {
				get: function () {
					return storage;
				}
			},
			$signalR: {
				get: function () {
					return signalR;
				}
			},
			$notifier: {
				get: function () {
					return notifier;
				}
			},
			$setTitle: {
				get: function() {
					return setTitle;
				}
			},
			$log: {
				get: () => Data.log
			},
			$tabsManager: {
				get: () => tabsManager.instance
			},
			$creativeBlobsDb: {
				get: () => creativeBlobsDb
			},
			$globals: {
				get: () => globals
			},
			$isAICreative: {
				get: () => isAICreative
			},
			$audioHelper: {
				get: () => audioHelper
			},
			$logOnce: {
				get: () => logOnce
			},
			$releaseUI: {
				get: () => releaseUI
			}
		});
		Vue.prototype.$userInfo = Vue.observable(Auth.userInfo);
	},
	setConfig,
	checkVersion,
	setVueApp,
	getVueApp,
	eventsBus,
	setNotifier,
	notify,
	notifyError,
	notifyInfo,
	notifyMessage,
	notifySuccess,
	notifyWarning,
	setPreventNotifications,
	confirmAction,
	creativeBlobsDb,
	tabsManager,
	isAICreative,
	releaseUI
};
